/**
 * ActivityContext — connection-scoped activity feed + inbound invites (Phase D).
 *
 * Feed is derived server-side (presence + connection events + actionable
 * invites), already filtered for block/invisible. Inert until
 * `social.invites_feed` is on.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useUser } from './UserContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { activityService } from '../services/api/activityService';
import { inviteService, type SendInviteRequest } from '../services/api/inviteService';
import { messagingSignalRService } from '../services/signalr/MessagingSignalRService';
import type { ActivityEntry, ConnectionInvite } from '../types/social';

interface ActivityContextValue {
  feed: ActivityEntry[];
  invites: ConnectionInvite[];
  isLoading: boolean;
  refresh: () => Promise<void>;
  sendInvite: (req: SendInviteRequest) => Promise<void>;
  acceptInvite: (inviteId: string) => Promise<{ roomId?: string; matchId?: string } | null>;
  declineInvite: (inviteId: string) => Promise<void>;
  /** Apply a live invite push (called by the hub handler). */
  applyInvite: (invite: ConnectionInvite) => void;
  /** Remove an expired invite (called by the hub handler). */
  removeInvite: (inviteId: string) => void;
}

const ActivityContext = createContext<ActivityContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export function ActivityProvider({ children }: ProviderProps) {
  const { user } = useUser();
  const enabled = useFeatureFlag('social.invites_feed');
  const [feed, setFeed] = useState<ActivityEntry[]>([]);
  const [invites, setInvites] = useState<ConnectionInvite[]>([]);
  const [isLoading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled || !user?.id) {
      setFeed([]);
      setInvites([]);
      return;
    }
    setLoading(true);
    try {
      const [feedEntries, inboundInvites] = await Promise.all([
        activityService.getActivity(),
        inviteService.getInboundInvites(),
      ]);
      setFeed(feedEntries);
      setInvites(inboundInvites);
    } catch {
      setFeed([]);
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, user?.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const sendInvite = useCallback(
    async (req: SendInviteRequest) => {
      await inviteService.sendInvite(req);
    },
    []
  );

  const acceptInvite = useCallback(async (inviteId: string) => {
    const result = await inviteService.acceptInvite(inviteId);
    setInvites((list) => list.filter((i) => i.id !== inviteId));
    return result ? { roomId: result.roomId, matchId: result.matchId } : null;
  }, []);

  const declineInvite = useCallback(async (inviteId: string) => {
    await inviteService.declineInvite(inviteId);
    setInvites((list) => list.filter((i) => i.id !== inviteId));
  }, []);

  const applyInvite = useCallback((invite: ConnectionInvite) => {
    setInvites((list) =>
      list.some((i) => i.id === invite.id) ? list : [invite, ...list]
    );
  }, []);

  const removeInvite = useCallback((inviteId: string) => {
    setInvites((list) => list.filter((i) => i.id !== inviteId));
  }, []);

  // Subscribe to live invite pushes on the shared hub (merges with the
  // presence handlers registered by PresenceContext).
  useEffect(() => {
    if (!enabled) return;
    messagingSignalRService.setEventHandlers({
      onReceiveInvite: applyInvite,
      onInviteExpired: removeInvite,
    });
    return () => {
      messagingSignalRService.setEventHandlers({
        onReceiveInvite: undefined,
        onInviteExpired: undefined,
      });
    };
  }, [enabled, applyInvite, removeInvite]);

  const value = useMemo<ActivityContextValue>(
    () => ({
      feed,
      invites,
      isLoading,
      refresh,
      sendInvite,
      acceptInvite,
      declineInvite,
      applyInvite,
      removeInvite,
    }),
    [feed, invites, isLoading, refresh, sendInvite, acceptInvite, declineInvite, applyInvite, removeInvite]
  );

  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}

export function useActivity(): ActivityContextValue {
  const ctx = useContext(ActivityContext);
  if (!ctx) throw new Error('useActivity must be used within an ActivityProvider');
  return ctx;
}
